import { Drawer, List } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import { PromoCodeTermsDrawerProps } from 'components/terms-and-conditions-drawers/promo-code-terms/types'
import parse from 'html-react-parser'
import { PROMO_CODE_KEY_MAPPING } from 'components/terms-and-conditions-drawers/promo-code-terms/PromoCodeTermsDrawer.constants'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { PRODUCT_PROMO_CODES, PRODUCT_PROMOS } from 'components/promo-code/constants'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { InputGroupKeys } from 'store/customer/enums'
import { CUSTOMER_TYPES, LANGUAGES, PRODUCTS_CONFIG } from '@boltenergy-be/shared'

const PromoCodeTermsDrawer = ({ code, ...props }: PromoCodeTermsDrawerProps) => {
  // Redux
  const { isCompany } = useSelectInputGroup(InputGroupKeys.PERSONAL_DATA)
  const customerType = isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential

  // i18n
  const { t } = useTranslation('simulation')

  // Router
  const { locale } = useRouter()

  // Constants
  const specificConditions = code ? PROMO_CODE_KEY_MAPPING[code]?.[locale === Language.FRENCH ? Language.FRENCH : Language.DUTCH] : null

  // Plenty promo specifics
  // Not included in the specific conditions mapping as it's a dynamic promo code that changes every month but the text remains the same.
  const isPlentyPromoCode = code && PRODUCT_PROMO_CODES.has(code)
  const productLinkedToPromo = isPlentyPromoCode
    ? Object.values(PRODUCT_PROMOS).find((promo) => promo.code[customerType] === code)?.product
    : undefined
  const productNameForPromo = productLinkedToPromo
    ? PRODUCTS_CONFIG[productLinkedToPromo]?.name?.[locale === Language.FRENCH ? LANGUAGES.FR : LANGUAGES.NL]
    : undefined
  const plentyOnlinePromoConditions =
    isPlentyPromoCode && productNameForPromo
      ? (t('terms.promo.plentyPromo.conditions', { returnObjects: true, productName: productNameForPromo }) as string[])
      : null

  return (
    <Drawer {...props} title={t('terms.promo.title')}>
      <section className="flex-col gap-400">
        <p>{t('terms.promo.general.description')}</p>
        <List as="ul">
          {(t('terms.promo.general.bullets', { returnObjects: true, context: isPlentyPromoCode ? 'oneOff' : 'spread' }) as string[]).map(
            (text, idx) => (
              <li key={`general_${idx}`}>{text}</li>
            )
          )}
        </List>
      </section>

      {(specificConditions || plentyOnlinePromoConditions) && (
        <section className="flex-col gap-400">
          <p>
            {plentyOnlinePromoConditions
              ? t('terms.promo.plentyPromo.title')
              : parse(t('terms.promo.specificConditions.description', { code }))}
          </p>
          <List as="ul">
            {(specificConditions || plentyOnlinePromoConditions)?.map((text, idx) => (
              <li key={`specific_${idx}`}>{text}</li>
            ))}
          </List>
        </section>
      )}
    </Drawer>
  )
}

export default PromoCodeTermsDrawer
